import { useState, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, RefreshCw, FileText, Table2, Minus, Plus, Sparkles, TrendingUp, IndianRupee, Users, Wheat, AlertCircle, School as SchoolIcon, FileStack, Printer } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { calculateReport } from "../lib/mdmCalculator";
import { getMenuDisplay, isLaddooActive, isValidServiceDate, CATEGORY_DISPLAY, dayName, formatCurrency, formatQty } from "../lib/mdmData";
import { exportPDF, exportExcel, exportBulkPDF, exportCompactPDF } from "../lib/mdmExport";
import { academicYearForDate } from "../lib/academicYear";
import { api } from "../lib/api";
import { getSchoolInfo, saveSchoolInfo } from "../lib/schoolInfo";
import SchoolInfoDialog from "../components/SchoolInfoDialog";
import PremiumModal from "../components/PremiumModal";
import PromotionalAd, { PromotionalAdModal, shouldShowPromotionalModal } from "../components/PromotionalAd";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const CATS = ["balvatika", "primary", "upper_primary", "high_school"];

export default function Calculator() {
  const [date, setDate] = useState(new Date());
  const [counts, setCounts] = useState({ balvatika: 0, primary: 0, upper_primary: 0, high_school: 0 });
  const [result, setResult] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [overrides, setOverrides] = useState([]);
  const [schoolDialogOpen, setSchoolDialogOpen] = useState(false);
  const [schoolInfo, setSchoolInfo] = useState(getSchoolInfo());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const reportRef = useRef(null);
  const { user } = useAuth();
  const nav = useNavigate();
  const [premium, setPremium] = useState(false);
  useEffect(() => {
    if (!user) { setPremium(false); return; }
    api.subMe().then((r) => setPremium(!!r?.is_premium)).catch(() => setPremium(false));
  }, [user]);

  const requireBulk = () => {
    if (premium) return setBulkOpen(true);
    toast("Bulk PDF is a Premium feature — Launch Offer ₹299/year unlocks it.");
    nav("/premium");
  };

  useEffect(() => {
    api.listRateOverrides().then(setOverrides).catch(() => setOverrides([]));
  }, []);

  const valid = isValidServiceDate(date);
  const menu = useMemo(() => getMenuDisplay(date), [date]);
  const laddoo = useMemo(() => isLaddooActive(date, date.getDay()), [date]);
  const ayear = useMemo(() => academicYearForDate(date), [date]);
  const yearHasOverride = useMemo(() => overrides.some((o) => o.academic_year === ayear), [overrides, ayear]);
  const totalStudents = counts.balvatika + counts.primary + counts.upper_primary + counts.high_school;

  const setCount = (k, v) => setCounts((prev) => ({ ...prev, [k]: Math.max(0, Math.min(9999, v || 0)) }));

  const [usage, setUsage] = useState({ premium: false, used: 0, remaining: 7, free_limit: 7, limit_reached: false });
  const [premiumModalOpen, setPremiumModalOpen] = useState(false);
  const [promoModalOpen, setPromoModalOpen] = useState(false);
  useEffect(() => {
    api.reportUsage().then(setUsage).catch(() => {});
  }, [user, premium]);

  const generate = () => {
    if (!valid) return toast.error("Select a date between 2012 and 2100");
    if (totalStudents === 0) return toast.error("Enter at least one student count");
    // Pre-flight limit check (bounces to Premium modal if reached; premium users skip)
    if (!usage.premium && usage.limit_reached) {
      setPremiumModalOpen(true);
      return;
    }
    setSchoolDialogOpen(true);
  };

  const finalizeGenerate = async (info) => {
    saveSchoolInfo(info);
    setSchoolInfo(info);
    // Increment the free-report counter on the backend (server-side enforced)
    try {
      const u = await api.reportCheckAndIncrement();
      setUsage(u);
    } catch (e) {
      if (e?.response?.status === 402) {
        setSchoolDialogOpen(false);
        setUsage((prev) => ({ ...prev, limit_reached: true, used: e.response.data?.detail?.used ?? 7 }));
        setPremiumModalOpen(true);
        return;
      }
      // Any other network error — allow the generation to proceed (fail open)
    }
    try {
      const r = calculateReport(date, counts, overrides);
      r.school = info;
      setResult(r);
      setSchoolDialogOpen(false);
      toast.success("Report generated");
      // Silent save to Poshan Panji (per-UDISE daily register). Non-blocking.
      saveToPanji(r, info).catch(() => { /* silent — user still has the report */ });
      setTimeout(() => reportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      // Show the promotional modal at most once per session, after a real
      // report is generated — never on page load.
      if (shouldShowPromotionalModal()) {
        setTimeout(() => setPromoModalOpen(true), 3500);
      }
    } catch (e) {
      toast.error(e.message || "Calculation failed");
    }
  };

  const saveToPanji = async (r, info) => {
    if (!info?.udise || !/^\d{11}$/.test(info.udise)) return;
    const yyyy = r.date.getFullYear();
    const mm = String(r.date.getMonth() + 1).padStart(2, "0");
    const dd = String(r.date.getDate()).padStart(2, "0");
    await api.savePanjiEntry({
      udise: info.udise,
      school_name: info.name || "",
      date_iso: `${yyyy}-${mm}-${dd}`,
      weekday_name: r.weekday_name,
      academic_year: r.academic_year,
      menu_display: r.menu_display,
      counts,
      summary: r.summary,
      aggregated_items: r.aggregated_items,
      categories: r.categories,
    });
  };

  const reset = () => {
    setDate(new Date());
    setCounts({ balvatika: 0, primary: 0, upper_primary: 0, high_school: 0 });
    setResult(null);
    toast.info("Form reset");
  };

  const onPDF = async () => {
    if (!result) return toast.error("Generate the report first");
    try { await exportPDF(result); toast.success("PDF downloaded"); } catch (e) { toast.error("PDF export failed"); console.error(e); }
  };
  const onCompactPDF = async () => {
    if (!result) return toast.error("Generate the report first");
    try { await exportCompactPDF(result); toast.success("Compact 1-page PDF downloaded"); } catch (e) { toast.error("Compact PDF export failed"); console.error(e); }
  };
  const onExcel = () => {
    if (!result) return toast.error("Generate the report first");
    try { exportExcel(result); toast.success("Excel downloaded"); } catch (e) { toast.error("Excel export failed"); console.error(e); }
  };

  const runBulk = async ({ fromDate, toDate }) => {
    if (totalStudents === 0) { toast.error("Enter attendance first — this count is used for every day in the range"); return; }
    const info = getSchoolInfo();
    if (!info.name || !info.udise) {
      toast.error("Save school details first — click Generate Report and fill the dialog once");
      return;
    }
    setBulkBusy(true);
    try {
      await exportBulkPDF({ fromDate, toDate, counts, overrides, school: info });
      toast.success("Bulk PDF downloaded");
      setBulkOpen(false);
    } catch (e) {
      toast.error(e.message || "Bulk export failed");
      console.error(e);
    } finally {
      setBulkBusy(false);
    }
  };

  // Auto-recalculate if there's a report visible
  useEffect(() => {
    if (result) {
      try {
        const r = calculateReport(date, counts, overrides);
        r.school = schoolInfo;
        setResult(r);
      } catch (e) { /* ignore */ }
    }
     
  }, [date, counts, overrides]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="calculator-page">
      {/* Title */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="chip chip-green"><Sparkles size={14}/> PM POSHAN Daily Calculator</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold text-slate-900">MDM quantity &amp; cooking cost</h1>
          <p className="mt-1 text-slate-600">Enter today’s attendance across four categories. Rates auto-apply from the 2012–2100 rate periods.</p>
        </div>
      </div>

      {/* Date + Menu */}
      <div className="mt-6 grid md:grid-cols-5 gap-4">
        <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-white p-5">
          <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">📅 Meal date</Label>
          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full h-12 mt-2 justify-start text-left font-semibold" data-testid="calc-date-btn">
                <CalendarIcon size={16}/> {format(date, "EEEE, dd MMM yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={date} onSelect={(d) => { if (d) { setDate(d); setDatePickerOpen(false); }}}
                        fromYear={2012} toYear={2100} captionLayout="dropdown-buttons"/>
            </PopoverContent>
          </Popover>
          <div className="mt-2 flex items-center gap-2 text-xs">
            <span className="chip chip-slate" data-testid="calc-ayear">AY {ayear}</span>
            {yearHasOverride && <span className="chip chip-saffron" data-testid="calc-override-active">⚙ Custom rates active</span>}
          </div>
          {!valid && (
            <div className="mt-3 text-sm text-red-600 flex items-center gap-1.5" data-testid="date-error"><AlertCircle size={14}/> Rates not available. Choose a date between 2012 and 2100.</div>
          )}
        </div>
        <div className="md:col-span-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-emerald-800">👨‍🍳 Today’s menu · {dayName(date)}</Label>
              <div className="mt-2 font-display font-extrabold text-lg text-emerald-900" data-testid="calc-menu">{menu}</div>
            </div>
            {laddoo && (
              <div className="chip chip-saffron shrink-0" data-testid="laddoo-badge">🧁 Laddoo day</div>
            )}
          </div>
        </div>
      </div>

      {/* Students */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">👩‍🎓 Present students</Label>
            <div className="mt-1 font-display font-bold text-slate-900">Enter attendance in each category</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Total present</div>
            <div className="font-display font-extrabold text-2xl text-emerald-700" data-testid="calc-total-students">{totalStudents}</div>
          </div>
        </div>
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {CATS.map((key) => {
            const d = CATEGORY_DISPLAY[key];
            return (
              <div key={key} className={`rounded-xl border-2 p-4 ${d.ui.soft}`} data-testid={`cat-card-${key}`}>
                <div className="flex items-center gap-2">
                  <span className="text-2xl leading-none">{d.emoji}</span>
                  <div className={`font-display font-bold text-sm ${d.ui.accent}`}>{d.name}</div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Button variant="outline" size="icon" className="h-11 w-11 rounded-lg border-slate-300 bg-white"
                          onClick={() => setCount(key, counts[key] - 1)} data-testid={`stepper-dec-${key}`}>
                    <Minus size={16}/>
                  </Button>
                  <Input
                    type="number"
                    min="0"
                    max="9999"
                    value={counts[key]}
                    onChange={(e) => setCount(key, Number(e.target.value))}
                    className="h-11 text-center font-display text-lg font-bold bg-white"
                    data-testid={`count-${key}`}
                  />
                  <Button variant="outline" size="icon" className="h-11 w-11 rounded-lg border-slate-300 bg-white"
                          onClick={() => setCount(key, counts[key] + 1)} data-testid={`stepper-inc-${key}`}>
                    <Plus size={16}/>
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center">
          {!usage.premium && (
            <div className="text-sm flex items-center gap-2" data-testid="free-reports-indicator">
              <span className="chip chip-slate text-[10px]">🎁 Free Reports: {usage.used} / {usage.free_limit}</span>
              {usage.remaining === 1 && <span className="text-orange-700 font-semibold text-xs">Only 1 free report remaining</span>}
              {usage.limit_reached && <span className="text-red-700 font-semibold text-xs">Free limit reached — unlock Premium</span>}
            </div>
          )}
          {usage.premium && (
            <span className="chip chip-green text-[10px]" data-testid="free-reports-premium-badge">👑 Premium · unlimited</span>
          )}
          <Button
            onClick={generate}
            size="lg"
            disabled={totalStudents === 0 || !valid}
            className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white h-12 text-base font-semibold"
            data-testid="generate-btn"
          >
            Generate Report ✅
          </Button>
          <Button
            onClick={reset}
            size="lg"
            variant="outline"
            className="sm:w-auto h-12 text-base"
            data-testid="reset-btn"
          >
            <RefreshCw size={16}/> Reset
          </Button>
        </div>
      </div>

      {/* Report */}
      {result && (
        <div ref={reportRef} className="mt-10 space-y-6 fade-rise" data-testid="report-section">
          {/* KPI cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Kpi icon={<CalendarIcon size={16}/>} label="Date" value={format(result.date, "d MMM yy")} tint="slate"/>
            <Kpi label="Day" value={result.weekday_name} tint="slate"/>
            <Kpi icon={<Users size={16}/>} label="Students" value={result.summary.total_students} tint="emerald" testid="kpi-students"/>
            <Kpi icon={<IndianRupee size={16}/>} label="Total cost" value={formatCurrency(result.summary.grand_total)} tint="orange" testid="kpi-total-cost"/>
            <Kpi icon={<TrendingUp size={16}/>} label="Avg / student" value={formatCurrency(result.summary.avg_per_student)} tint="orange" testid="kpi-avg"/>
            <Kpi icon={<Wheat size={16}/>} label="Total rice" value={`${result.summary.total_rice_kg} kg`} tint="emerald" testid="kpi-rice"/>
          </div>

          {/* Aggregated table */}
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="chip chip-green">📊 Aggregated</div>
                <div className="mt-2 font-display font-bold text-lg text-slate-900">Ingredients summary (all categories)</div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button onClick={onPDF} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="export-pdf-btn">
                  <FileText size={16}/> PDF
                </Button>
                <Button onClick={onCompactPDF} variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="export-compact-btn">
                  <Printer size={16}/> 1-Page
                </Button>
                <Button onClick={onExcel} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="export-excel-btn">
                  <Table2 size={16}/> Excel
                </Button>
                <Button onClick={requireBulk} variant="outline" className="border-emerald-700 text-emerald-800 hover:bg-emerald-50" data-testid="export-bulk-btn">
                  <FileStack size={16}/> Bulk PDF {!premium && <span className="chip chip-saffron ml-1 text-[9px] py-0 px-1">Premium</span>}
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <Th>Item</Th><Th>Total qty</Th><Th>Avg rate</Th><Th className="text-right">Total amount</Th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(result.aggregated_items)
                    .sort((a, b) => b[1].total_cost - a[1].total_cost)
                    .map(([n, d]) => (
                      <tr key={n} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`agg-row-${n}`}>
                        <Td><span className="mr-1.5">{d.emoji}</span><span className="font-semibold text-slate-900">{n}</span></Td>
                        <Td>{formatQty(d.total_quantity, d.unit)}</Td>
                        <Td>{formatCurrency(d.avg_rate)}</Td>
                        <Td className="text-right font-semibold text-slate-900">{formatCurrency(d.total_cost)}</Td>
                      </tr>
                    ))}
                </tbody>
                <tfoot>
                  <tr className="bg-emerald-50">
                    <Td colSpan={3} className="font-bold text-emerald-900 text-right">Grand Total</Td>
                    <Td className="text-right font-display font-extrabold text-lg text-emerald-800" data-testid="grand-total">{formatCurrency(result.summary.grand_total)}</Td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Category-wise breakdown */}
          {Object.entries(result.categories)
            .filter(([, c]) => c.student_count > 0)
            .map(([key, cat]) => (
              <CategoryCard key={key} categoryKey={key} data={cat} />
            ))}
        </div>
      )}

      {/* Native ad below the report — appears once a report exists */}
      {result && (
        <div className="mt-8" data-testid="calculator-post-report-ad">
          <PromotionalAd placement="report" variant="card"/>
        </div>
      )}

      <SchoolInfoDialog
        open={schoolDialogOpen}
        onOpenChange={setSchoolDialogOpen}
        initial={schoolInfo}
        onConfirm={finalizeGenerate}
      />

      <BulkExportDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        busy={bulkBusy}
        onRun={runBulk}
      />

      <PremiumModal
        open={premiumModalOpen}
        onClose={() => setPremiumModalOpen(false)}
        used={usage.used}
        freeLimit={usage.free_limit}
      />

      <PromotionalAdModal open={promoModalOpen} onClose={() => setPromoModalOpen(false)} placement="modal"/>
    </div>
  );
}

function Kpi({ icon, label, value, tint, testid }) {
  const bg = tint === "emerald" ? "border-emerald-200 bg-emerald-50" : tint === "orange" ? "border-orange-200 bg-orange-50" : "border-slate-200 bg-white";
  return (
    <div className={`rounded-xl border p-3 ${bg}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">{icon}{label}</div>
      <div className="mt-1 font-display font-extrabold text-lg text-slate-900">{value}</div>
    </div>
  );
}

function CategoryCard({ categoryKey, data }) {
  const d = CATEGORY_DISPLAY[categoryKey];
  return (
    <div className={`rounded-2xl border-2 ${d.ui.soft} overflow-hidden`} data-testid={`report-cat-${categoryKey}`}>
      <div className={`p-5 flex items-center justify-between ${d.ui.solid}`}>
        <div className="flex items-center gap-2">
          <span className="text-2xl">{d.emoji}</span>
          <div>
            <div className="font-display font-bold text-white">{d.name}</div>
            <div className="text-xs text-white/90">{data.student_count} {data.student_count === 1 ? "student" : "students"} · per-student ₹{data.per_student_total.toFixed(2)}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-white/80 font-bold">Total Amount</div>
          <div className="font-display font-extrabold text-2xl text-white" data-testid={`cat-total-${categoryKey}`}>{formatCurrency(data.subtotal)}</div>
        </div>
      </div>
      <div className="overflow-x-auto bg-white">
        <table className="w-full text-left">
          <thead className={`${d.ui.soft} border-b border-slate-200`}>
            <tr>
              <Th>Item</Th><Th>Per student</Th><Th>Rate</Th><Th>Total qty</Th><Th className="text-right">Amount</Th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(data.items)
              .sort((a, b) => b[1].total_cost - a[1].total_cost)
              .map(([n, it]) => (
                <tr key={n} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`cat-${categoryKey}-item-${n}`}>
                  <Td>
                    <span className="mr-1.5">{it.emoji}</span>
                    <span className="font-semibold text-slate-900">{n}</span>
                    {it.is_auto_balanced && <span className="ml-2 chip chip-saffron text-[10px]" title="Auto-balanced">⚖ Auto</span>}
                  </Td>
                  <Td>{formatQty(it.qty_per_student, it.unit)}</Td>
                  <Td>{formatCurrency(it.rate_per_student)}</Td>
                  <Td>{formatQty(it.total_quantity, it.unit)}</Td>
                  <Td className="text-right font-semibold">{formatCurrency(it.total_cost)}</Td>
                </tr>
              ))}
          </tbody>
          <tfoot>
            <tr className={`${d.ui.soft} border-t-2 border-slate-200`}>
              <Td colSpan={4} className={`font-display font-bold text-right ${d.ui.accent}`}>Total Amount</Td>
              <Td className={`text-right font-display font-extrabold text-lg ${d.ui.accent}`} data-testid={`cat-footer-total-${categoryKey}`}>{formatCurrency(data.subtotal)}</Td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// SchoolInfoDialog moved to /components/SchoolInfoDialog.jsx and imported at the top.

const Th = ({ children, className = "", ...rest }) => <th {...rest} className={`p-3 text-xs uppercase tracking-wider font-bold text-slate-500 ${className}`}>{children}</th>;
const Td = ({ children, className = "", colSpan, ...rest }) => <td {...rest} colSpan={colSpan} className={`p-3 text-sm text-slate-700 ${className}`}>{children}</td>;

// ------------------------------------------------------------------
// Bulk export dialog: pick date range (week/month/custom)
// ------------------------------------------------------------------
function BulkExportDialog({ open, onOpenChange, busy, onRun }) {
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - ((today.getDay() + 6) % 7));  // Monday
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);

  const [mode, setMode] = useState("month");
  const [fromDate, setFromDate] = useState(startOfMonth);
  const [toDate, setToDate] = useState(endOfMonth);

  useEffect(() => {
    if (!open) return;
    if (mode === "week") { setFromDate(startOfWeek); setToDate(endOfWeek); }
    else if (mode === "month") { setFromDate(startOfMonth); setToDate(endOfMonth); }
    // custom keeps whatever user picked
     
  }, [mode, open]);

  const days = Math.max(0, Math.ceil((toDate - fromDate) / 86400000) + 1);

  const submit = () => {
    if (!(fromDate instanceof Date) || !(toDate instanceof Date)) { toast.error("Pick valid dates"); return; }
    if (toDate < fromDate) { toast.error("End date must be after start date"); return; }
    if (days > 100) { toast.error("Range too large (max 100 days)"); return; }
    onRun({ fromDate, toDate });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bulk-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <span className="h-8 w-8 rounded-lg bg-emerald-700 text-white grid place-items-center"><FileStack size={16}/></span>
            Bulk PDF — one page per day
          </DialogTitle>
          <DialogDescription>Picks the current attendance and generates a consolidated PDF with a summary sheet + one page per day in the range.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Range</Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {[
                { k: "week", l: "This week" },
                { k: "month", l: "This month" },
                { k: "custom", l: "Custom" },
              ].map((r) => (
                <button
                  key={r.k}
                  onClick={() => setMode(r.k)}
                  data-testid={`bulk-range-${r.k}`}
                  className={`h-11 rounded-md text-sm font-semibold border ${
                    mode === r.k ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-700 border-slate-300 hover:border-emerald-400"
                  }`}
                >
                  {r.l}
                </button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="bulk-from" className="text-xs font-bold uppercase tracking-wider text-slate-500">From date</Label>
              <Input id="bulk-from" type="date" value={format(fromDate, "yyyy-MM-dd")} onChange={(e) => { setMode("custom"); setFromDate(new Date(e.target.value)); }}
                     className="h-11 mt-1.5" data-testid="bulk-from-date"/>
            </div>
            <div>
              <Label htmlFor="bulk-to" className="text-xs font-bold uppercase tracking-wider text-slate-500">To date</Label>
              <Input id="bulk-to" type="date" value={format(toDate, "yyyy-MM-dd")} onChange={(e) => { setMode("custom"); setToDate(new Date(e.target.value)); }}
                     className="h-11 mt-1.5" data-testid="bulk-to-date"/>
            </div>
          </div>

          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900" data-testid="bulk-summary">
            <b>{days}</b> day(s) selected. Current attendance is applied to every day; the day&rsquo;s menu is used automatically.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="bulk-generate-btn">
            {busy ? "Generating…" : "Generate Bulk PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
